import React, { useId, useRef } from 'react';
import { formatPhoneFieldValue, phoneDigitsOnly } from '../../utils/phoneFormat';

type PhoneInputProps = {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (formatted: string) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
};

/** Téléphone FR : espaces insérés automatiquement tous les 2 chiffres pendant la saisie. */
const PhoneInput: React.FC<PhoneInputProps> = ({
  label,
  placeholder = '06 12 34 56 78',
  value,
  onChange,
  error,
  disabled = false,
  required = false,
  className = '',
  id,
}) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const selectionStart = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = phoneDigitsOnly(input.value.slice(0, selectionStart)).length;

    const digits = phoneDigitsOnly(input.value).slice(0, 10);
    const formatted = formatPhoneFieldValue(digits);
    onChange(formatted);

    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;

      let caret = 0;
      let seenDigits = 0;
      while (caret < formatted.length && seenDigits < digitsBeforeCaret) {
        if (/\d/.test(formatted[caret])) {
          seenDigits += 1;
        }
        caret += 1;
      }
      if (seenDigits === digitsBeforeCaret && formatted[caret] === ' ') {
        caret += 1;
      }
      el.setSelectionRange(caret, caret);
    });
  };

  const inputClasses = `
    w-full border rounded-md transition-all duration-normal focus:outline-none focus:ring-2 focus:ring-offset-0
    px-4 py-3 text-base
    ${error ? 'border-error-300 focus:border-error-500 focus:ring-error-500' : 'border-neutral-300 focus:border-primary-500 focus:ring-primary-500'}
    ${disabled ? 'bg-neutral-50 text-neutral-500 cursor-not-allowed' : 'bg-white text-neutral-900'}
    ${className}
  `.trim();

  return (
    <div className="w-full">
      {label ? (
        <label htmlFor={inputId} className="mb-2 block text-sm font-medium text-neutral-700">
          {label}
          {required ? <span className="ml-1 text-error-500">*</span> : null}
        </label>
      ) : null}

      <input
        ref={inputRef}
        id={inputId}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        maxLength={14}
        className={inputClasses}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${inputId}-error` : undefined}
      />

      {error ? (
        <p id={`${inputId}-error`} className="mt-1 text-sm text-error-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default PhoneInput;
